import type { BookingLanguageCode } from '../config/i18n';
import {
  getGeoCountryForLocale,
  normalizeDetectedCountry,
  type BookingGeoCountryCode,
} from '../config/geoLocale';

export const DETECTED_GEO_COUNTRY_STORAGE_KEY = 'detectedGeoCountry';
export const DETECTED_GEO_AT_STORAGE_KEY = 'detectedGeoCountryAt';
/** Préférence manuelle (Settings) — prioritaire sur la détection auto. */
export const PREFERRED_GEO_COUNTRY_STORAGE_KEY = 'preferredGeoCountry';
/** Re-détection après 24 h (changement de pays / voyage). */
export const DETECTED_GEO_TTL_MS = 24 * 60 * 60 * 1000;

export const GEO_COUNTRY_API_PATH = '/api/geo-country';

function readCachedDetectedCountry(): BookingGeoCountryCode | null {
  try {
    const code = localStorage.getItem(DETECTED_GEO_COUNTRY_STORAGE_KEY);
    const at = localStorage.getItem(DETECTED_GEO_AT_STORAGE_KEY);
    if (!code || !at) return null;
    const age = Date.now() - parseInt(at, 10);
    if (!Number.isFinite(age) || age > DETECTED_GEO_TTL_MS) return null;
    return normalizeDetectedCountry(code);
  } catch {
    return null;
  }
}

function persistDetectedCountry(code: BookingGeoCountryCode): void {
  try {
    localStorage.setItem(DETECTED_GEO_COUNTRY_STORAGE_KEY, code);
    localStorage.setItem(DETECTED_GEO_AT_STORAGE_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function readPreferredGeoCountry(): BookingGeoCountryCode | null {
  try {
    return normalizeDetectedCountry(localStorage.getItem(PREFERRED_GEO_COUNTRY_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function persistPreferredGeoCountry(code: BookingGeoCountryCode): void {
  try {
    localStorage.setItem(PREFERRED_GEO_COUNTRY_STORAGE_KEY, code);
  } catch {
    /* ignore */
  }
}

export function fallbackGeoCountry(language: string): BookingGeoCountryCode {
  return getGeoCountryForLocale(language) as BookingGeoCountryCode;
}

/** Détecte le pays via edge Netlify ; null si indisponible ou hors marchés supportés. */
export async function fetchDetectedGeoCountry(): Promise<BookingGeoCountryCode | null> {
  try {
    const resp = await fetch(GEO_COUNTRY_API_PATH, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!resp.ok) return null;
    const data = (await resp.json()) as { country?: string | null };
    return normalizeDetectedCountry(data.country);
  } catch {
    return null;
  }
}

export async function resolveGeoCountry(
  language: BookingLanguageCode
): Promise<BookingGeoCountryCode> {
  const preferred = readPreferredGeoCountry();
  if (preferred) return preferred;

  const cached = readCachedDetectedCountry();
  if (cached) return cached;

  const detected = await fetchDetectedGeoCountry();
  if (detected) {
    persistDetectedCountry(detected);
    return detected;
  }

  return fallbackGeoCountry(language);
}

export function readCachedGeoCountryOrFallback(
  language: BookingLanguageCode
): BookingGeoCountryCode {
  return (
    readPreferredGeoCountry() ??
    readCachedDetectedCountry() ??
    fallbackGeoCountry(language)
  );
}
